import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys, LIVE_REFETCH_MS } from "@/api/queryClient";
import type { Agent } from "@/types";

// Pattern (M1): queryFn instanziiert apiClient on-call (statt im Hook-Body),
// damit Auth-Session-Hydration nicht verpasst wird. `enabled: !!session`
// verhindert Fetch ohne Token.

export function useAgents(opts?: { enabled?: boolean }): UseQueryResult<Agent[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: queryKeys.list("agents"),
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      // ?all=true: Admin-Verwaltungsliste zeigt ALLE Agents inkl. offline.
      // Ohne den Flag versteckt der Master frisch erstellte (noch offline)
      // Agents — dann wirkt die Liste nach dem Anlegen fälschlich leer.
      return client.get<Agent[]>("/agents?all=true");
    },
    // #299 (Review M-3): der Aufrufer darf den Abruf unterdruecken, wenn die Sicht
    // gar nicht freigegeben ist — sonst feuert die Sidebar auf JEDER Seite garantierte
    // 403er (drei pro Seitenaufruf, mit retry + refetchOnWindowFocus).
    enabled: !!session && (opts?.enabled ?? true),
    refetchInterval: LIVE_REFETCH_MS,
  });
}

export function useAgent(id: string | undefined): UseQueryResult<Agent> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.detail("agents", id) : ["resources", "agents", "_invalid"],
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<Agent>(`/agents/${id}`);
    },
    enabled: !!id && !!session,
    refetchInterval: LIVE_REFETCH_MS,
  });
}
